import React, { useState, useEffect } from 'react';
import Slider from 'react-slider';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import TooltipHelper from './TooltipHelper';

const TimelineFilter = ({ 
  year, 
  month, 
  yearOptions, 
  onYearChange, 
  onMonthChange,
  yearFrom,
  monthFrom,
  yearTo,
  monthTo,
  onYearFromChange,
  onMonthFromChange,
  onYearToChange,
  onMonthToChange
}) => {
  const months = [
    { value: '', label: 'Todos', short: 'Todos' },
    { value: '1', label: 'Enero', short: 'Ene' },
    { value: '2', label: 'Febrero', short: 'Feb' },
    { value: '3', label: 'Marzo', short: 'Mar' },
    { value: '4', label: 'Abril', short: 'Abr' },
    { value: '5', label: 'Mayo', short: 'May' },
    { value: '6', label: 'Junio', short: 'Jun' },
    { value: '7', label: 'Julio', short: 'Jul' },
    { value: '8', label: 'Agosto', short: 'Ago' },
    { value: '9', label: 'Septiembre', short: 'Sep' },
    { value: '10', label: 'Octubre', short: 'Oct' },
    { value: '11', label: 'Noviembre', short: 'Nov' },
    { value: '12', label: 'Diciembre', short: 'Dic' },
  ];

  // Estado para el modo de filtro
  const [filterMode, setFilterMode] = useState('timeline'); // 'timeline' o 'range'
  
  // Estado para controlar expansión de secciones
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const [rangeExpanded, setRangeExpanded] = useState(false);

  // Estado para el mes seleccionado formateado (modo timeline)
  const [selectedMonthFrom, setSelectedMonthFrom] = useState('');
  const [selectedMonthTo, setSelectedMonthTo] = useState('');
  const [sliderTouched, setSliderTouched] = useState(false);

  // Función para formatear la fecha en el formato "MMMM yyyy"
  const formatDate = (yearValue, monthValue) => {
    if (!yearValue || !monthValue || monthValue === '' || monthValue === '0') return '';
    const monthNum = Number(monthValue);
    if (monthNum < 1 || monthNum > 12) return '';
    const firstDateOfYear = new Date(yearValue, monthNum - 1, 1);
    return format(firstDateOfYear, 'MMMM yyyy', { locale: es });
  };

  // Actualizar cuando cambia el mes (modo timeline) - puede ser un rango
  useEffect(() => {
    if (year && month && month !== '' && month !== '0') {
      // Si month tiene formato "1-9" (rango), parsearlo
      const monthParts = month.split('-');
      if (monthParts.length === 2) {
        const fromMonth = monthParts[0];
        const toMonth = monthParts[1];
        if (fromMonth !== '0' && toMonth !== '0') {
          setSelectedMonthFrom(formatDate(Number(year), fromMonth));
          setSelectedMonthTo(formatDate(Number(year), toMonth));
        } else {
          setSelectedMonthFrom('');
          setSelectedMonthTo('');
        }
      } else {
        // Mes individual
        const monthNum = Number(month);
        if (monthNum > 0 && monthNum <= 12) {
          const formatted = formatDate(Number(year), month);
          setSelectedMonthFrom(formatted);
          setSelectedMonthTo(formatted);
        } else {
          setSelectedMonthFrom('');
          setSelectedMonthTo('');
        }
      }
    } else {
      setSelectedMonthFrom('');
      setSelectedMonthTo('');
    }
  }, [year, month]);


  // Función para cambiar entre modos
  const handleModeChange = (mode) => {
    setFilterMode(mode);
    // Limpiar filtros del modo anterior
    if (mode === 'timeline') {
      onYearFromChange?.('');
      onMonthFromChange?.('');
      onYearToChange?.('');
      onMonthToChange?.('');
    } else {
      onYearChange('');
      onMonthChange('');
      setSelectedMonthFrom('');
      setSelectedMonthTo('');
      setSliderTouched(false);
    }
  };

  // Función para manejar el cambio en el selector de año (modo timeline)
  const handleYearChange = (newYear) => {
    onYearChange(newYear);
    onMonthChange(''); // Resetear mes al cambiar año
    setSelectedMonthFrom('');
    setSelectedMonthTo('');
    setSliderTouched(false);
  };

  // Función para manejar el cambio en el slider de meses (modo timeline) - rango
  const handleMonthSliderChange = (value) => {
    if (!year) return;
    
    // El slider devuelve un array con [min, max] para rango
    const [fromMonth, toMonth] = Array.isArray(value) ? value : [value, value];
    
    // Si ambos son 0, significa "Todos"
    if (fromMonth === 0 && toMonth === 0) {
      onMonthChange('');
      setSelectedMonthFrom('');
      setSelectedMonthTo('');
      setSliderTouched(false);
    } else {
      setSliderTouched(true);
      // Formato: "mesInicio-mesFin" (ej: "1-9" para enero a septiembre)
      const monthValue = fromMonth === toMonth ? String(fromMonth) : `${fromMonth}-${toMonth}`;
      onMonthChange(monthValue);
      setSelectedMonthFrom(formatDate(Number(year), String(fromMonth)));
      setSelectedMonthTo(formatDate(Number(year), String(toMonth)));
    }
  };

  // Obtener valores del slider desde el mes seleccionado
  const getSliderValue = () => {
    if (!month) return [0, 0];
    const monthParts = month.split('-');
    if (monthParts.length === 2) {
      return [Number(monthParts[0]), Number(monthParts[1])];
    }
    const monthNum = Number(month);
    return [monthNum, monthNum];
  };

  // Función para limpiar el rango (modo range)
  const handleClearRange = () => {
    onYearFromChange?.('');
    onMonthFromChange?.('');
    onYearToChange?.('');
    onMonthToChange?.('');
  };

  return (
    <div className="space-y-2">
      {/* Modo Línea de tiempo - Desplegable */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setTimelineExpanded(!timelineExpanded)}
          className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-[#0488AF]" />
            <label className="text-sm font-semibold text-gray-800">Línea de tiempo</label>
            <TooltipHelper text="Permite filtrar por un año específico y dentro de ese año puedes seleccionar un rango de meses desplazando los círculos por la línea de tiempo, cuando están en &quot;Todos&quot; se verá el año completo." />
          </div>
          {timelineExpanded ? (
            <ChevronUp size={16} className="text-[#0488AF]" />
          ) : (
            <ChevronDown size={16} className="text-[#0488AF]" />
          )}
        </button>
        {timelineExpanded && (
          <div className="p-3 space-y-3 border-t border-gray-200 bg-gray-50">
            {/* Filtro de Año - Selector dropdown */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Año</label>
              <div className="relative flex-1 max-w-[200px]">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                <select
                  value={year}
                  onChange={(e) => handleYearChange(e.target.value)}
                  className="w-full h-10 pl-10 pr-8 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none text-sm appearance-none cursor-pointer hover:border-gray-400 transition-colors"
                  title="Año"
                >
                  {yearOptions.map((y, i) => (
                    <option key={i} value={y}>
                      {y || 'Todos los años'}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
              {year && (
                <button
                  onClick={() => handleYearChange('')}
                  className="inline-flex items-center justify-center h-8 px-3 rounded-full border border-[#DC3465] text-[#DC3465] text-xs font-semibold tracking-wide uppercase hover:bg-[#C42251] hover:text-white transition-colors"
                >
                  Limpiar año
                </button>
              )}
            </div>

            {/* Filtro de Mes - Solo si hay año seleccionado */}
            {year && (
              <div className="bg-white rounded-xl p-5 border-2 border-gray-200 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <label className="text-base font-bold text-gray-800">Rango de meses</label>
                <button
                  onClick={() => {
                    onMonthChange('');
                    setSelectedMonthFrom('');
                    setSelectedMonthTo('');
                    setSliderTouched(false);
                  }}
                  className="inline-flex items-center justify-center h-8 px-3 rounded-full border border-[#09274A] text-[#09274A] text-xs font-semibold tracking-wide uppercase hover:bg-[#09274A] hover:text-white transition-colors"
                >
                  Año completo
                </button>
              </div>

              {/* Campos de entrada de fechas - más prominentes */}
              <div className="mb-5">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1.5 font-medium">Desde</label>
                    <input
                      type="text"
                      value={selectedMonthFrom || (year ? 'Todos' : '')}
                      placeholder={year ? 'Todos' : 'enero 2025'}
                      readOnly
                      className={`w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg bg-white text-gray-800 cursor-default font-medium focus:outline-none focus:border-[#0488AF] transition-colors ${
                        (!selectedMonthFrom && year) || selectedMonthFrom === 'Todos' ? '' : 'lowercase'
                      }`}
                    />
                  </div>
                  <div className="pt-6">
                    <span className="text-gray-400 font-bold text-xl">-</span>
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-600 mb-1.5 font-medium">Hasta</label>
                    <input
                      type="text"
                      value={selectedMonthTo || (year ? 'Todos' : '')}
                      placeholder={year ? 'Todos' : 'agosto 2025'}
                      readOnly
                      className={`w-full px-4 py-3 text-sm border-2 border-gray-300 rounded-lg bg-white text-gray-800 cursor-default font-medium focus:outline-none focus:border-[#0488AF] transition-colors ${
                        (!selectedMonthTo && year) || selectedMonthTo === 'Todos' ? '' : 'lowercase'
                      }`}
                    />
                  </div>
                </div>
              </div>

              {/* Slider de meses (rango) - más visible */}
              <div className="px-2 py-4">
                <Slider
                  min={0}
                  max={12}
                  step={1}
                  value={getSliderValue()}
                  onChange={handleMonthSliderChange}
                  renderTrack={(props, state) => {
                    const { key, ref, ...trackProps } = props;
                    return (
                      <div
                        key={key}
                        ref={ref}
                        {...trackProps}
                        className="h-1 rounded-full bg-gray-300"
                        style={{
                          ...trackProps.style,
                          height: '4px',
                          borderRadius: '9999px',
                        }}
                      />
                    );
                  }}
                  renderThumb={(props, state) => {
                    const { key, ref, ...thumbProps } = props;
                    const isRight = state.index === 1;
                    return (
                      <div
                        key={key}
                        ref={ref}
                        {...thumbProps}
                        className={`rounded-full shadow-lg cursor-grab active:cursor-grabbing focus:outline-none focus:ring-2 focus:ring-[#0488AF] focus:ring-offset-2 hover:scale-110 transition-transform ${
                          isRight
                            ? 'w-7 h-7 bg-[#0488AF] border-2 border-white ring-2 ring-[#0488AF] ring-offset-1'
                            : 'w-6 h-6 bg-white border-2 border-[#0488AF]'
                        }`}
                        style={{
                          ...thumbProps.style,
                        }}
                      />
                    );
                  }}
                />
                
                {/* Etiquetas de todos los meses - posicionadas según el slider */}
                <div className="mt-5 relative px-2" style={{ height: '60px' }}>
                  {/* Etiqueta "Todos" al inicio (posición 0 del slider) */}
                  <span
                    className={`absolute text-[10px] md:text-xs font-semibold transition-colors whitespace-nowrap ${
                      !month
                        ? 'text-[#0488AF]'
                        : 'text-gray-500'
                    }`}
                    style={{ 
                      left: '0%',
                      top: '0px'
                    }}
                  >
                    Todos
                  </span>
                  {/* Etiquetas de meses posicionadas uniformemente - VERTICALES */}
                  {months.filter(m => m.value !== '').map((m) => {
                    const monthNum = Number(m.value);
                    const [from, to] = getSliderValue();
                    const isInRange = month && from > 0 && monthNum >= from && monthNum <= to;
                    // Calcular posición: el slider va de 0 a 12 (13 posiciones)
                    // 0 = "Todos", 1-12 = meses (enero a diciembre)
                    // react-slider usa la fórmula: posición = ((value - min) / (max - min)) * 100
                    // Para monthNum (1-12): posición = ((monthNum - 0) / (12 - 0)) * 100 = (monthNum / 12) * 100
                    // Esta es la misma fórmula que usa react-slider para posicionar los thumbs
                    // Las etiquetas están en un contenedor con px-2, igual que el slider, así que están alineadas
                    const position = (monthNum / 12) * 100;
                    return (
                      <span
                        key={m.value}
                        className={`absolute text-[10px] md:text-xs font-semibold transition-colors whitespace-nowrap ${
                          isInRange
                            ? 'text-[#0488AF]'
                            : 'text-gray-500'
                        }`}
                        title={m.label}
                        style={{ 
                          left: `${position}%`,
                          top: '20px',
                          transform: 'translateX(-50%) rotate(-90deg)',
                          transformOrigin: 'center center',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {m.short}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
            )}
          </div>
        )}
      </div>

      {/* Modo Rango de fecha - Desplegable */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <button
          onClick={() => setRangeExpanded(!rangeExpanded)}
          className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-[#0488AF]" />
            <label className="text-sm font-semibold text-gray-800">Rango de fechas</label>
            <TooltipHelper text="Puedes filtrar por un rango de fecha amplio, ya que seleccionas el año y mes de inicio (Desde) independientes del año y mes de fin (Hasta), por ejemplo, si seleccionas 'Marzo 2024' hasta 'Mayo 2025', se mostrarán todas las reuniones de ese periodo de 14 meses." />
            {(yearFrom || yearTo) && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearRange();
                }}
                className="text-xs text-gray-500 hover:text-gray-700 transition-colors ml-2"
              >
                Limpiar
              </button>
            )}
          </div>
          {rangeExpanded ? (
            <ChevronUp size={16} className="text-[#0488AF]" />
          ) : (
            <ChevronDown size={16} className="text-[#0488AF]" />
          )}
        </button>
        {rangeExpanded && (
          <div className="p-3 space-y-3 border-t border-gray-200 bg-gray-50">

          {/* Mensaje de ayuda */}
          <div className="inline-flex items-center gap-2 rounded-lg border-2 border-blue-300 bg-blue-50 px-4 py-2 text-xs sm:text-sm font-medium text-blue-800 shadow-sm mb-2">
            <span className="text-base leading-none">ℹ️</span>
            <span>
              <span className="font-bold text-blue-900">Importante:</span> El mes es obligatorio cuando se selecciona un año. Debes seleccionar el mes tanto en "Desde" como en "Hasta" para aplicar el filtro.
            </span>
          </div>

          {/* Campos de entrada de fechas */}
          <div className="flex items-center gap-3">
            {/* Desde */}
            <div className="flex-1 space-y-2">
              <label className="text-xs text-gray-600 flex items-center gap-1">
                Desde <span className="text-red-500">*</span>
                <TooltipHelper text="El mes es obligatorio cuando seleccionas un año. Debes seleccionar el mes para que el filtro funcione correctamente." />
              </label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                  <select
                    value={yearFrom || ''}
                    onChange={(e) => onYearFromChange?.(e.target.value)}
                    className="w-full h-10 pl-9 pr-8 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none text-sm appearance-none cursor-pointer hover:border-gray-400 transition-colors"
                  >
                    <option value="">Año</option>
                    {yearOptions.filter(y => y).map((y, i) => (
                      <option key={i} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {yearFrom && (
                  <div className="relative flex-1">
                    <select
                      value={monthFrom || ''}
                      onChange={(e) => onMonthFromChange?.(e.target.value)}
                      required
                      className={`w-full h-10 pl-3 pr-8 rounded-lg border bg-white focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none text-sm appearance-none cursor-pointer hover:border-gray-400 transition-colors ${
                        yearFrom && !monthFrom ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      title="El mes es obligatorio cuando seleccionas un año"
                    >
                      <option value="">Mes *</option>
                      {months.filter(m => m.value).map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Separador */}
            <div className="pt-6">
              <span className="text-gray-400 font-medium">-</span>
            </div>

            {/* Hasta */}
            <div className="flex-1 space-y-2">
              <label className="text-xs text-gray-600 flex items-center gap-1">
                Hasta <span className="text-red-500">*</span>
                <TooltipHelper text="El mes es obligatorio cuando seleccionas un año. Debes seleccionar el mes para que el filtro funcione correctamente." />
              </label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                  <select
                    value={yearTo || ''}
                    onChange={(e) => onYearToChange?.(e.target.value)}
                    className="w-full h-10 pl-9 pr-8 rounded-lg border border-gray-300 bg-white focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none text-sm appearance-none cursor-pointer hover:border-gray-400 transition-colors"
                  >
                    <option value="">Año</option>
                    {yearOptions.filter(y => y).map((y, i) => (
                      <option key={i} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
                {yearTo && (
                  <div className="relative flex-1">
                    <select
                      value={monthTo || ''}
                      onChange={(e) => onMonthToChange?.(e.target.value)}
                      required
                      className={`w-full h-10 pl-3 pr-8 rounded-lg border bg-white focus:ring-2 focus:ring-[#0488AF] focus:border-[#0488AF] outline-none text-sm appearance-none cursor-pointer hover:border-gray-400 transition-colors ${
                        yearTo && !monthTo ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                      title="El mes es obligatorio cuando seleccionas un año"
                    >
                      <option value="">Mes *</option>
                      {months.filter(m => m.value).map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelineFilter;
